/**
 * Secure cookie configuration for the application
 */

export interface CookieConfig {
  httpOnly?: boolean;
  secure?: boolean;
  sameSite?: 'strict' | 'lax' | 'none';
  maxAge?: number;
  path?: string;
}

/**
 * Default secure cookie configuration
 */
export const SECURE_COOKIE_CONFIG: CookieConfig = {
  httpOnly: true,
  secure: true, // Requires HTTPS in production
  sameSite: 'strict',
  path: '/',
  maxAge: 7 * 24 * 60 * 60 // 7 days in seconds
};

/**
 * Session cookie configuration (shorter duration)
 */
export const SESSION_COOKIE_CONFIG: CookieConfig = {
  httpOnly: true,
  secure: true,
  sameSite: 'strict',
  path: '/',
  maxAge: 24 * 60 * 60 // 24 hours in seconds
};

/**
 * Create a secure cookie string
 */
export function createSecureCookie(
  name: string, 
  value: string, 
  config: CookieConfig = SECURE_COOKIE_CONFIG
): string {
  let cookieString = `${name}=${value}`;
  
  if (config.maxAge) {
    cookieString += `; Max-Age=${config.maxAge}`;
  }
  
  if (config.path) {
    cookieString += `; Path=${config.path}`;
  }
  
  if (config.sameSite) {
    cookieString += `; SameSite=${config.sameSite}`;
  }
  
  if (config.secure) {
    cookieString += `; Secure`;
  }
  
  if (config.httpOnly) {
    cookieString += `; HttpOnly`;
  }
  
  return cookieString;
}

/**
 * Check if we're in a secure context (HTTPS or localhost)
 */
export function isSecureContext(): boolean {
  return (
    window.location.protocol === 'https:' ||
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1'
  );
}